import React, { useState, useEffect } from 'react';
import { TextField } from '@material-ui/core';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import format from 'date-fns/format';
import moment from 'moment';
interface CustomDateInputProps {
  value: string | null; // Expected as 'yyyy-MM-dd'
  onChange: (formattedDate: string) => void;
  disabled?: boolean;
  maxDate?: Date;
  minDate?: Date;
  label?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

const monthMap: Record<string, string> = {
  JAN: '01',
  FEB: '02',
  MAR: '03',
  APR: '04',
  MAY: '05',
  JUN: '06',
  JUL: '07',
  AUG: '08',
  SEP: '09',
  OCT: '10',
  NOV: '11',
  DEC: '12',
};

const CustomDateInput: React.FC<CustomDateInputProps> = ({
  value,
  onChange,
  disabled = false,
  maxDate,
  minDate,
  label,
  name,
  className,
  placeholder = 'e.g. 02APR2025',
  error,
  helperText,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [localError, setLocalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');



  useEffect(() => {
    if (value) {
      const parsedDate = moment.utc(value, "YYYY-MM-DD").local(true).format("DDMMMyyyy").toUpperCase();
      setInputValue(parsedDate);
    } else {
      setInputValue('');
    }
  }, [value]);

  const parseDateInput = (input: string): string | null => {
    const match = input.toUpperCase().match(/^(\d{2})([A-Z]{3})(\d{4})$/);
    if (!match) {
      setErrorMessage('Invalid format. Expected DDMMMYYYY (e.g., 02APR2025)');
      return null;
    }

    const [, day, mon, year] = match;
    const month = monthMap[mon];
    if (!month) {
      setErrorMessage('Invalid month in date.');
      return null;
    }

    const parsedDate = parse(`${day}-${month}-${year}`, 'dd-MM-yyyy', new Date());

    if (!isValid(parsedDate)) {
      setErrorMessage('Invalid date value.');
      return null;
    }

    if (minDate && parsedDate < minDate) {
      setErrorMessage(`Date must be after ${format(minDate, 'dd MMM yyyy')}`);
      return null;
    }

    if (maxDate && parsedDate > maxDate) {
      setErrorMessage(`Date must be before ${format(maxDate, 'dd MMM yyyy')}`);
      return null;
    }

    return format(parsedDate, 'yyyy-MM-dd');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase();
    setInputValue(raw);
  
    if (raw.trim() === '') {
      if (required) {
        setLocalError(true);
        setErrorMessage('This field is required.');
      } else {
        setLocalError(false);
        setErrorMessage('');
      }
      onChange(''); // Clear the value
      return;
    }
  
    const formatted = parseDateInput(raw);
    if (formatted) {
      setLocalError(false);
      setErrorMessage('');
      onChange(formatted);
    } else {
      setLocalError(true);
    }
  };
  

  return (
    <TextField
      label={required ? `${label} *` : label}
      name={name}
      className={`${className} ${required ? 'bd-errorMsg' : ''}`}
      value={inputValue}
      onChange={handleChange}
      placeholder={placeholder}
      disabled={disabled}
      error={localError || error}
      helperText={localError ? (helperText || errorMessage) : helperText}
      inputProps={{ maxLength: 9 }}
      FormHelperTextProps={{
        style: {
          whiteSpace: 'normal', // Allow wrapping
          wordBreak: 'break-word', // Break long strings
          maxWidth: '100%', // Limit width to input field width
        },
      }}
    />
  );
};

export default CustomDateInput;
